/**
 * gpu-detect.js — canonical GPU detector (etalon). `window.GpuDetect`.
 *
 * Why this module exists
 * ----------------------
 * ~11 tools each re-implemented `canvas.getContext('webgl')` + read of
 * `WEBGL_debug_renderer_info.UNMASKED_RENDERER_WEBGL`. All but gpu-benchmark
 * did it WITHOUT `powerPreference`, so on a hybrid laptop/desktop (iGPU + dGPU)
 * the browser hands back the LOW-POWER integrated GPU and the dedicated card is
 * never reported. Bug report 2026-05-28 (/es/gaming-pc-scanner): "tiene una
 * grafica dedicada y solo dedecta la igpu".
 *
 * The fix is a DUAL-PROBE: ask the browser for the high-performance context AND
 * the low-power context. A hybrid machine returns two different renderers
 * (dGPU + iGPU); a single-GPU machine returns the same string twice. WebGPU's
 * `requestAdapter({powerPreference})` is queried too — it corroborates the
 * vendor/architecture and is the only signal left when WebGL's debug-renderer
 * extension is masked by a privacy browser.
 *
 * Contract
 * --------
 *   GpuDetect.scan()  → Promise<result>   (memoized; runs the probes once)
 *   GpuDetect.parse(renderer) → {model, tier, score, kind, approx}
 *   GpuDetect.classifyKind(renderer, vendor) → 'dedicated'|'integrated'|'unknown'
 *   GpuDetect.cleanVendor(vendor, renderer) → brand string fit for display
 *   GpuDetect.bench()  → Promise<{ok, gops, tier, ms}>  (memoized ~300ms probe)
 *
 * result = {
 *   gpus:       [ {renderer, vendor, model, tier, score, kind, source, architecture, approx} ],
 *   dedicated:  gpu|null,
 *   integrated: gpu|null,
 *   primary:    gpu,              // dedicated || top-score; what callers should score
 *   isHybrid:   bool,            // a dedicated AND an integrated GPU were both found
 *   masked:     bool,            // renderer hidden/generic (privacy browser, SwiftShader)
 *   approx:     bool,            // renderer is a browser GENERALIZATION, not the real
 *                                //   card: Firefox default buckets every GPU into a
 *                                //   canonical model + ", or similar" suffix (an RTX
 *                                //   4060 reports as "GTX 980, or similar"). Callers
 *                                //   MUST NOT present the model as the user's card.
 *   method:     'webgl+webgpu'|'webgl'|'webgpu'|'none'
 * }
 *
 * bench() measures REAL shader throughput (fill+ALU fullscreen passes with a
 * readPixels sync). It exists because the renderer STRING lies (approx buckets,
 * masked privacy modes) while the silicon underneath still runs at native
 * speed — measurement is the only trustworthy classifier there. ~300ms on the
 * main thread; call it lazily and only when the string is unreliable.
 *
 * NEVER throws — every probe is wrapped; a total failure resolves to
 * {method:'none', masked:true, primary:<unknown>} so callers can render
 * best-effort and never block the user (feedback_never_block_user_access).
 *
 * DO NOT route fingerprint-engine.js / console-detect.js through this. They
 * deliberately read the DEFAULT (iGPU) renderer as a stable fingerprint /
 * UA-classification vector — forcing high-performance would corrupt that signal.
 *
 * Loaded WITH defer. Callers MUST invoke scan() inside an event handler or
 * after DOMContentLoaded, never at inline-script parse time
 * (feedback_no_load_race_for_parse_time_globals).
 *
 * Written ES2018-safe (var/function, no ?./??) — deploy esbuild transpiles
 * public/js to es2018, and we keep it readable on old Yandex/iOS regardless.
 */
(function () {
    'use strict';

    if (window.GpuDetect) return; // duplicate <script> guard

    /* ── Tier table ──────────────────────────────────────────────────────────
     * score is the GPU sub-score on the gaming-pc-scanner 0–30 scale; other
     * tools only read .tier / .model. Ordered most- to least-specific. */
    function parse(renderer) {
        var r = String(renderer || '').toUpperCase();
        var kind = classifyKind(renderer, '');
        var model = cleanModel(renderer);
        // Firefox (default, no privacy hardening needed) generalizes every GPU
        // into a canonical bucket and appends ", or similar" — the model is a
        // CLASS representative, not the user's card. Detected here so every
        // caller can label it honestly instead of asserting the wrong model.
        var approx = /,?\s*or\s+similar\s*\)?\s*$/i.test(String(renderer || ''));
        var T;

        // ── NVIDIA consumer ──
        if (/RTX\s*50\d{2}/.test(r))              T = ['Ultra', 30];
        else if (/RTX\s*40\d{2}/.test(r))         T = ['Ultra', 29];
        else if (/RTX\s*30\d{2}/.test(r))         T = ['High', 27];
        else if (/RTX\s*20\d{2}/.test(r))         T = ['High', 24];
        else if (/GTX\s*16\d{2}/.test(r))         T = ['Mid', 20];
        else if (/GTX\s*10\d{2}/.test(r))         T = ['Mid', 17];
        else if (/GTX\s*[789]\d{2}/.test(r))      T = ['Mid', 14];
        // ── NVIDIA workstation / laptop / legacy (previously all "Unknown") ──
        else if (/RTX\s*A[4-6]\d{3}/.test(r))     T = ['Ultra', 28]; // RTX A4000+
        else if (/RTX\s*A[23]\d{3}/.test(r))      T = ['High', 24];
        else if (/RTX\s*A[01]?\d{3}\b/.test(r))   T = ['Mid', 18];   // A500/A1000 laptop
        else if (/QUADRO\s*RTX/.test(r))          T = ['High', 24];
        else if (/TITAN/.test(r))                 T = ['High', 26];
        else if (/QUADRO/.test(r))                T = ['Mid', 15];   // P/M/K pro cards
        else if (/NVIDIA\s*T\d{3,4}\b/.test(r))   T = ['Mid', 15];   // T600/T1000/T1200
        else if (/\bMX\s*\d{3}\b/.test(r))        T = ['Entry', 11]; // GeForce MX laptop
        else if (/\bGT\s*10[23]0\b/.test(r))      T = ['Entry', 11]; // GT 1030
        else if (/\bGTS?\s*[2-9]\d{2}M?\b/.test(r)) T = ['Entry', 8]; // GT 610..GT 940M
        // ── AMD discrete ──
        else if (/RX\s*9\d{3}/.test(r))           T = ['Ultra', 29]; // RDNA4
        else if (/RX\s*7\d{3}/.test(r))           T = ['Ultra', 28];
        else if (/RX\s*6\d{3}/.test(r))           T = ['High', 25];
        else if (/RX\s*5\d{3}/.test(r))           T = ['Mid', 22];
        else if (/RX\s*[45]\d{2}\b/.test(r))      T = ['Mid', 16];
        else if (/RADEON\s*(PRO|VII)/.test(r))    T = ['High', 24];
        // ── AMD RDNA iGPU (7xxM/8xxM) + semi-custom (Steam Deck) ──
        else if (/RADEON(\(TM\))?\s*8\d{2}M/.test(r)) T = ['Mid', 16];   // 880M/890M
        else if (/RADEON(\(TM\))?\s*7\d{2}M/.test(r)) T = ['Entry', 13]; // 780M
        else if (/RADEON(\(TM\))?\s*6\d{2}M/.test(r)) T = ['Entry', 11];
        else if (/AMD\s*CUSTOM/.test(r))          T = ['Mid', 16];   // Steam Deck / console APU
        // ── Intel discrete (tolerate "Arc(TM) A770" separators) ──
        else if (/ARC.{0,8}B\d{3}/.test(r))       T = ['Mid', 19];
        else if (/ARC.{0,8}A\d{3}/.test(r))       T = ['Mid', 18];
        // ── Intel Arc iGPU: Lunar Lake 130V/140V, Arrow Lake 130T/140T,
        //    and the bare "Intel(R) Arc(TM) Graphics" Meteor Lake branding ──
        else if (/ARC.{0,10}1[34]\d\s*V/.test(r)) T = ['Mid', 15];
        else if (/ARC.{0,10}1[34]\d\s*T/.test(r)) T = ['Entry', 13];
        else if (/ARC(\(TM\))?\s*GRAPHICS/.test(r)) T = ['Entry', 13];
        // ── Qualcomm Windows-on-ARM (Snapdragon X) — before generic Adreno ──
        else if (/ADRENO.{0,8}X[12]/.test(r))     T = ['Mid', 15];
        // ── Apple SoC ──
        else if (/APPLE\s*M[345]/.test(r))        T = ['High', 28];
        else if (/APPLE\s*M2/.test(r))            T = ['High', 24];
        else if (/APPLE\s*M1/.test(r))            T = ['Mid', 20];
        else if (/APPLE\s*A1[5-9]/.test(r))       T = ['Mid', 16]; // A15+ iPhone/iPad
        else if (/APPLE\s*A\d{2}/.test(r))        T = ['Entry', 12];
        else if (/APPLE/.test(r))                 T = ['Mid', 18];
        // ── Mobile (Android) ──
        else if (/ADRENO[^\d]{0,6}[78]\d{2}/.test(r)) T = ['Mid', 16];
        else if (/ADRENO[^\d]{0,6}6\d{2}/.test(r)) T = ['Entry', 12];
        else if (/ADRENO/.test(r))                T = ['Entry', 9];
        else if (/MALI|POWERVR|XCLIPSE|IMMORTALIS/.test(r)) T = ['Entry', 10];
        // ── GPU architecture names (WebGPU adapter.info fallback when WebGL is
        //    masked — Chrome reports vendor+architecture, not the model number) ──
        else if (/BLACKWELL|ADA(\b|-|\s|LOVELACE)/.test(r)) T = ['Ultra', 28];
        else if (/AMPERE|TURING/.test(r))         T = ['High', 25];
        else if (/RDNA.?4/.test(r))               T = ['Ultra', 28];
        else if (/RDNA.?3/.test(r))               T = ['Ultra', 27];
        else if (/RDNA.?2/.test(r))               T = ['High', 24];
        // ── Integrated (Intel iGPU + AMD APU) ──
        else if (/IRIS\s*XE/.test(r))             T = ['Entry', 12];
        else if (/IRIS|UHD|\bHD\s*GRAPHICS|\bHD\s*[4-6]\d{2}/.test(r)) T = ['Entry', 10];
        else if (/VEGA\s*\d{1,2}\b|RADEON(\(TM\))?\s*GRAPHICS|RADEON\s*VEGA/.test(r)) T = ['Entry', 11];
        // ── Virtual machines / remote sessions (real GPU invisible from inside) ──
        else if (/VMWARE|VIRTUALBOX|\bVBOX|VIRGL|PARALLELS|\bQXL\b|HYPER-V|MICROSOFT\s*REMOTE/.test(r)) T = ['Virtual', 8];
        // ── Software / unknown ──
        else if (/SWIFTSHADER|LLVMPIPE|SOFTWARE|MICROSOFT\s*BASIC|MESA\s*OFFSCREEN/.test(r)) T = ['Software', 5];
        else                                      T = ['Unknown', 15];

        return { model: model || (renderer ? String(renderer) : ''), tier: T[0], score: T[1], kind: kind, approx: approx };
    }

    /* ── Integrated vs dedicated ─────────────────────────────────────────── */
    function classifyKind(renderer, vendor) {
        var r = (String(renderer || '') + ' ' + String(vendor || '')).toUpperCase();
        if (!r.trim()) return 'unknown';
        // Software renderers are neither.
        if (/SWIFTSHADER|LLVMPIPE|SOFTWARE|MICROSOFT\s*BASIC|MESA\s*OFFSCREEN/.test(r)) return 'unknown';
        // Discrete cards. (NVIDIA vendor alone ⇒ discrete — their only PC iGPU is
        // Tegra, negligible on the web. Catches the WebGPU "nvidia ada-lovelace"
        // fallback string when WebGL's renderer is masked.)
        if (/RTX|GTX|QUADRO|TESLA|GEFORCE|NVIDIA/.test(r)) return 'dedicated';
        if (/\bRX\s*\d|RADEON\s*(PRO|VII|RX)|FIREPRO/.test(r)) return 'dedicated';
        if (/\bARC\b.{0,8}[AB]\d{2}/.test(r)) return 'dedicated'; // A770/B580 etc (numbered = discrete)
        // Integrated graphics.
        if (/IRIS|UHD|\bHD\s*GRAPHICS|\bHD\s*[4-6]\d{2}|INTEL.*GRAPHICS/.test(r)) return 'integrated';
        if (/VEGA|RADEON(\(TM\))?\s*GRAPHICS/.test(r)) return 'integrated';
        if (/ADRENO|MALI|POWERVR|XCLIPSE|IMMORTALIS|APPLE/.test(r)) return 'integrated'; // SoC GPUs
        return 'unknown';
    }

    /* ── Pull a clean model name out of an ANGLE / raw renderer string ────── */
    function cleanModel(renderer) {
        if (!renderer) return '';
        var s = String(renderer);
        // Firefox's approximation marker — parse() records it in `approx`; the
        // display name must not carry the raw ", or similar" tail around.
        s = s.replace(/,?\s*or\s+similar\s*(\))?\s*$/i, '$1');
        // ANGLE (Vendor, <Renderer> Direct3D11 vs_5_0 ps_5_0, D3D11)
        var m = s.match(/^ANGLE\s*\(([^,]+),\s*([\s\S]*?)\s*(?:Direct3D\d+|OpenGL[^,]*|Vulkan[^,]*|D3D\d+|vs_\d).*$/i);
        if (m) s = m[2];
        s = s.replace(/\((?:0x)?[0-9a-fA-F]{4,}\)/g, '');     // device id (0x....)
        s = s.replace(/\b(Direct3D\d+|D3D\d+|vs_\d_\d|ps_\d_\d)\b/gi, '');
        s = s.replace(/\((?:R|TM)\)/gi, '');                  // (R) / (TM)
        s = s.replace(/[(),]+\s*$/, '');
        s = s.replace(/\s{2,}/g, ' ').trim();
        return s;
    }

    /* ── Vendor string fit for display ────────────────────────────────────
     * Raw UNMASKED_VENDOR is noisy: Firefox reports "Google Inc. (NVIDIA)",
     * Chrome "Google Inc. (Intel)" — the real brand hides in the parens.
     * Falls back to deriving the brand from the renderer string when the
     * vendor field is a browser shell ("Mozilla", "WebKit", bare "Google"). */
    function cleanVendor(vendor, renderer) {
        var v = String(vendor || '').trim();
        var m = v.match(/\(([^()]+)\)\s*$/);
        if (m) v = m[1].trim();
        v = v.replace(/\s*(Inc|Corp|Corporation|Co|Ltd)\.?\s*$/i, '').trim();
        if (!v || /^(GOOGLE|MOZILLA|WEBKIT|APPLE COMPUTER)$/i.test(v)) {
            var r = String(renderer || '').toUpperCase();
            if (/NVIDIA|GEFORCE|RTX|GTX|QUADRO|TITAN/.test(r)) v = 'NVIDIA';
            else if (/AMD|RADEON/.test(r))                     v = 'AMD';
            else if (/INTEL|IRIS|UHD|\bARC\b/.test(r))         v = 'Intel';
            else if (/APPLE/.test(r))                          v = 'Apple';
            else if (/ADRENO|QUALCOMM/.test(r))                v = 'Qualcomm';
            else if (/MALI|IMMORTALIS/.test(r))                v = 'ARM';
            else if (/XCLIPSE/.test(r))                        v = 'Samsung';
            else if (/POWERVR/.test(r))                        v = 'Imagination';
        }
        return v;
    }

    /* ── WebGL single context probe ──────────────────────────────────────── */
    function probeWebGL(powerPref) {
        try {
            var canvas = document.createElement('canvas');
            var attrs = { powerPreference: powerPref, failIfMajorPerformanceCaveat: false };
            var gl = canvas.getContext('webgl2', attrs) ||
                     canvas.getContext('webgl', attrs) ||
                     canvas.getContext('experimental-webgl', attrs);
            if (!gl) return null;
            var renderer = '', vendor = '';
            var dbg = gl.getExtension('WEBGL_debug_renderer_info');
            if (dbg) {
                renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '';
                vendor = gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || '';
            }
            if (!renderer) {
                renderer = gl.getParameter(gl.RENDERER) || '';
                vendor = vendor || (gl.getParameter(gl.VENDOR) || '');
            }
            // Release the context immediately — two probes per page else leak.
            var lose = gl.getExtension('WEBGL_lose_context');
            if (lose) try { lose.loseContext(); } catch (_) {}
            if (!renderer) return null;
            return { renderer: String(renderer), vendor: String(vendor), source: 'webgl' };
        } catch (_) {
            return null;
        }
    }

    /* ── WebGPU single adapter probe ─────────────────────────────────────── */
    function probeWebGPU(powerPref) {
        if (!(navigator.gpu && navigator.gpu.requestAdapter)) return Promise.resolve(null);
        return navigator.gpu.requestAdapter({ powerPreference: powerPref }).then(function (adapter) {
            if (!adapter) return null;
            function fromInfo(info) {
                if (!info) return null;
                var vendor = info.vendor || '';
                var arch = info.architecture || '';
                var device = info.device || '';
                var desc = info.description || '';
                var renderer = desc || (device || (vendor + (arch ? ' ' + arch : '')));
                if (!renderer) return null;
                return {
                    renderer: String(renderer).trim(),
                    vendor: String(vendor).trim(),
                    architecture: String(arch).trim(),
                    source: 'webgpu'
                };
            }
            // Newer Chrome: synchronous adapter.info. Older: requestAdapterInfo().
            if (adapter.info) return fromInfo(adapter.info);
            if (adapter.requestAdapterInfo) {
                return adapter.requestAdapterInfo().then(fromInfo, function () { return null; });
            }
            return null;
        }, function () { return null; });
    }

    /* Resolve to null if a promise (e.g. a stuck WebGPU requestAdapter) does not
       settle in time — guarantees scan() always finishes and the UI never hangs. */
    function withTimeout(promise, ms) {
        return new Promise(function (resolve) {
            var done = false;
            var timer = setTimeout(function () { if (!done) { done = true; resolve(null); } }, ms);
            promise.then(
                function (v) { if (!done) { done = true; clearTimeout(timer); resolve(v); } },
                function () { if (!done) { done = true; clearTimeout(timer); resolve(null); } }
            );
        });
    }

    function norm(s) { return String(s || '').toUpperCase().replace(/[\s()]+/g, ' ').trim(); }

    function isMaskedRenderer(r) {
        var u = norm(r);
        if (!u) return true;
        return /SWIFTSHADER|LLVMPIPE|SOFTWARE|MICROSOFT BASIC|MESA OFFSCREEN|^WEBGL$|^MOZILLA$|^APPLE GPU$|^WEBKIT/.test(u);
    }

    /* Merge raw probe hits into deduped, parsed GPU entries.
       hits order is [glHP, glLP, gpuHP, gpuLP] so WebGL (richer model strings)
       is processed first; a WebGPU hit is only kept if it brings a kind WebGL
       did not already provide (e.g. fills a missing iGPU, or is the sole signal
       when WebGL is masked) — otherwise it's the same physical card again. */
    function buildEntries(hits) {
        var seen = {}, out = [];
        for (var i = 0; i < hits.length; i++) {
            var h = hits[i];
            if (!h || !h.renderer) continue;
            var key = norm(h.renderer);
            if (!key || seen[key]) continue;
            var p = parse(h.renderer);
            // classifyKind with vendor as well (catches "NVIDIA"-only vendor strings).
            var kind = p.kind !== 'unknown' ? p.kind : classifyKind(h.renderer, h.vendor);
            if (h.source === 'webgpu' && kind !== 'unknown') {
                var covered = false;
                for (var j = 0; j < out.length; j++) { if (out[j].kind === kind) { covered = true; break; } }
                if (covered) continue; // same kind already reported by a WebGL probe
            }
            seen[key] = true;
            out.push({
                renderer: h.renderer,
                vendor: h.vendor || '',
                model: p.model,
                tier: p.tier,
                score: p.score,
                kind: kind,
                source: h.source,
                architecture: h.architecture || '',
                approx: !!p.approx
            });
        }
        return out;
    }

    var _promise = null;

    function scan() {
        if (_promise) return _promise;
        _promise = new Promise(function (resolve) {
            // WebGL probes are synchronous; WebGPU are async.
            var glHP = probeWebGL('high-performance');
            var glLP = probeWebGL('low-power');
            Promise.all([
                withTimeout(probeWebGPU('high-performance'), 1500),
                withTimeout(probeWebGPU('low-power'), 1500)
            ]).then(function (gpuHits) {
                var hits = [glHP, glLP, gpuHits[0], gpuHits[1]];
                var gpus = buildEntries(hits);

                var dedicated = null, integrated = null;
                for (var i = 0; i < gpus.length; i++) {
                    if (!dedicated && gpus[i].kind === 'dedicated') dedicated = gpus[i];
                    if (!integrated && gpus[i].kind === 'integrated') integrated = gpus[i];
                }

                var primary;
                if (dedicated) primary = dedicated;
                else if (gpus.length) {
                    primary = gpus.slice().sort(function (a, b) { return b.score - a.score; })[0];
                } else {
                    primary = { renderer: '', vendor: '', model: '', tier: 'Unknown', score: 15, kind: 'unknown', source: 'none', architecture: '', approx: false };
                }

                var anyWebgl = !!(glHP || glLP);
                var anyWebgpu = !!(gpuHits[0] || gpuHits[1]);
                var method = anyWebgl && anyWebgpu ? 'webgl+webgpu'
                          : anyWebgl ? 'webgl'
                          : anyWebgpu ? 'webgpu'
                          : 'none';

                var masked = !gpus.length || isMaskedRenderer(primary.renderer);

                var result = {
                    gpus: gpus,
                    dedicated: dedicated,
                    integrated: integrated,
                    primary: primary,
                    isHybrid: !!(dedicated && integrated),
                    masked: masked,
                    approx: !!primary.approx,
                    method: method
                };

                emitOnce(result);
                resolve(result);
            }, function () {
                resolve(failResult());
            });
        }).catch(function () { return failResult(); });
        return _promise;
    }

    function failResult() {
        return {
            gpus: [],
            dedicated: null,
            integrated: null,
            primary: { renderer: '', vendor: '', model: '', tier: 'Unknown', score: 15, kind: 'unknown', source: 'none', architecture: '', approx: false },
            isHybrid: false,
            masked: true,
            approx: false,
            method: 'none'
        };
    }

    /* ── Throughput micro-bench ──────────────────────────────────────────
     * Fullscreen ALU-heavy passes with a 1px readPixels sync per batch —
     * measures the silicon, not the (possibly lying) renderer string.
     * Memoized. ~300ms main-thread cost; resolve({ok:false}) on any failure.
     * Tier thresholds are calibrated coarse buckets (see docs/gpu-detect-etalon.md):
     * software renderers land <1, iGPUs single digits, dGPUs tens-to-hundreds. */
    var _benchPromise = null;
    function bench() {
        if (_benchPromise) return _benchPromise;
        _benchPromise = new Promise(function (resolve) {
            var fail = { ok: false, gops: 0, tier: 'Unknown', ms: 0 };
            try {
                var canvas = document.createElement('canvas');
                canvas.width = 512; canvas.height = 512;
                var attrs = { powerPreference: 'high-performance', antialias: false, depth: false, stencil: false, preserveDrawingBuffer: false };
                var gl = canvas.getContext('webgl', attrs) || canvas.getContext('experimental-webgl', attrs);
                if (!gl) { resolve(fail); return; }
                function sh(type, src) {
                    var s = gl.createShader(type);
                    gl.shaderSource(s, src); gl.compileShader(s);
                    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
                }
                var vs = sh(gl.VERTEX_SHADER, 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}');
                var fs = sh(gl.FRAGMENT_SHADER,
                    'precision mediump float;uniform float u;void main(){' +
                    'float v=gl_FragCoord.x*1e-3+u;' +
                    'for(int i=0;i<64;i++){v=fract(sin(v*12.9898+u)*43758.5453);}' +
                    'gl_FragColor=vec4(v,fract(v*7.),fract(v*13.),1.);}');
                if (!vs || !fs) { resolve(fail); return; }
                var prog = gl.createProgram();
                gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
                if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { resolve(fail); return; }
                gl.useProgram(prog);
                var buf = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, buf);
                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
                var loc = gl.getAttribLocation(prog, 'p');
                gl.enableVertexAttribArray(loc);
                gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
                var uLoc = gl.getUniformLocation(prog, 'u');
                var px = new Uint8Array(4);
                // Warmup (pipeline compile / first submit) outside the timed window.
                gl.uniform1f(uLoc, 0.5);
                gl.drawArrays(gl.TRIANGLES, 0, 3);
                gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
                var draws = 0, batch = 4;
                var t0 = performance.now();
                var deadline = t0 + 240;
                while (performance.now() < deadline && draws < 6000) {
                    for (var i = 0; i < batch; i++) {
                        gl.uniform1f(uLoc, (draws + i) * 1e-4);
                        gl.drawArrays(gl.TRIANGLES, 0, 3);
                    }
                    draws += batch;
                    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px); // GPU sync
                    if (draws >= 32) batch = 16;
                }
                var ms = performance.now() - t0;
                // ops proxy: pixels × shader iterations × passes / time, in "Gops"
                var gops = ms > 0 ? (draws * (512 * 512) * 64) / (ms / 1000) / 1e9 : 0;
                var tier = gops >= 220 ? 'Ultra' : gops >= 90 ? 'High' : gops >= 25 ? 'Mid' : gops >= 5 ? 'Entry' : 'Software';
                var lose = gl.getExtension('WEBGL_lose_context');
                if (lose) try { lose.loseContext(); } catch (_) {}
                resolve({ ok: true, gops: Math.round(gops * 10) / 10, tier: tier, ms: Math.round(ms), draws: draws });
            } catch (e) {
                resolve(fail);
            }
        });
        return _benchPromise;
    }

    /* One-time telemetry so the dashboard can size the hybrid / masked cohorts. */
    var _emitted = false;
    function emitOnce(res) {
        if (_emitted || typeof window.__ae !== 'function') return;
        _emitted = true;
        try {
            // 'feature_use' (not a custom type) — AnalyticsEvent::ALLOWED_TYPES
            // drops unknown event types, so the convention is feature_use{f:...}.
            window.__ae('feature_use', {
                f: 'gpu_detect_scan',
                method: res.method,
                hybrid: res.isHybrid ? 1 : 0,
                masked: res.masked ? 1 : 0,
                approx: res.approx ? 1 : 0,
                n: res.gpus.length,
                ded: res.dedicated ? res.dedicated.tier : '',
                igpu: res.integrated ? res.integrated.tier : '',
                prim: res.primary ? res.primary.tier : '',
                // Truncated renderer string (a GPU model, not PII): the ONLY way
                // to learn which real-world strings still fall to Unknown tier
                // (29% of scans on 2026-07 data) and keep the table honest.
                rend: res.primary && res.primary.renderer ? String(res.primary.renderer).slice(0, 80) : ''
            });
        } catch (_) {}
    }

    window.GpuDetect = {
        scan: scan,
        parse: parse,
        classifyKind: classifyKind,
        cleanModel: cleanModel,
        cleanVendor: cleanVendor,
        bench: bench
    };
})();
